import { Plus, Server as ServerIcon, RefreshCw, Copy, Check } from "lucide-react";
import { useState } from "react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { PageHeader, ServerCard, AsyncBoundary, CardSkeleton, StatCard, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";

export default function Servers() {
  const { data: servers, loading, error, reload } = useAsync(() => api.getServers(), [], 20000);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", provider: "", region: "", description: "" });
  const [issued, setIssued] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [copied, setCopied] = useState(false);

  const summary = servers
    ? { online: servers.filter((s) => s.status === "online").length, total: servers.length, containers: servers.reduce((a, s) => a + s.containers, 0) }
    : {};

  const create = async (event) => {
    event.preventDefault(); setBusy(true); setActionError(null); setIssued(null);
    try { setIssued(await api.createServer(form)); setForm({ name: "", provider: "", region: "", description: "" }); reload(); }
    catch (err) { setActionError(err.detail || "Não foi possível cadastrar o servidor"); }
    finally { setBusy(false); }
  };
  const copy = async (value) => { await navigator.clipboard?.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); };

  return (
    <div className="space-y-6 animate-fade-up">
      <PageHeader
        icon={ServerIcon}
        title="Servidores"
        description="Gerencie todos os seus nós VPS. Arquitetura preparada para múltiplos servidores (sv1, sv2, sv3…)."
        breadcrumb={<><span>Infraestrutura</span><span>/</span><span className="text-foreground">Servidores</span></>}
        actions={<>
          <Button variant="outline" onClick={reload} className="border-line bg-transparent hover:bg-white/5" data-testid="servers-refresh"><RefreshCw className="h-4 w-4" /> Atualizar</Button>
          <Button onClick={() => { setOpen(true); setIssued(null); }} className="bg-brand text-white hover:bg-brand-hover" data-testid="servers-add"><Plus className="h-4 w-4" /> Adicionar Servidor</Button>
        </>}
      />

      {open && <form onSubmit={create} className="grid gap-3 rounded-xl border border-line bg-surface p-4 md:grid-cols-4">
        <input required placeholder="Nome administrativo" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded border border-line bg-background px-3 py-2 text-sm" />
        <input placeholder="Provedor" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} className="rounded border border-line bg-background px-3 py-2 text-sm" />
        <input placeholder="Região" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="rounded border border-line bg-background px-3 py-2 text-sm" />
        <input placeholder="Descrição" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded border border-line bg-background px-3 py-2 text-sm" />
        <div className="flex gap-2 md:col-span-4"><Button type="submit" disabled={busy}>{busy ? "Gerando…" : "Gerar bootstrap"}</Button><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancelar</Button></div>
        {actionError && <p className="text-sm text-err md:col-span-4">{actionError}</p>}
      </form>}

      {issued && <div className="space-y-3 rounded-xl border border-info/30 bg-info/5 p-4" data-testid="server-bootstrap-issued">
        <div><p className="font-medium text-foreground">Servidor criado; execute o comando no host alvo</p><p className="text-xs text-muted-foreground">O token é exibido somente nesta resposta e não será recuperado depois.</p></div>
        <div className="flex gap-2"><code className="min-w-0 flex-1 overflow-auto rounded bg-black/20 p-2 text-xs text-foreground">{issued.bootstrap_token}</code><Button type="button" size="sm" variant="outline" onClick={() => copy(issued.bootstrap_token)}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}</Button></div>
        <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-black/20 p-3 text-xs text-muted-foreground">{issued.install_command}</pre>
      </div>}

      {!loading && servers && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <StatCard icon={ServerIcon} label="Total de nós" value={String(summary.total)} accent />
          <StatCard icon={ServerIcon} label="Online" value={String(summary.online)} dots={summary.online > 0 ? [{ tone: "ok", label: "operacionais" }] : undefined} />
          <StatCard icon={ServerIcon} label="Containers" value={String(summary.containers)} />
          <StatCard icon={ServerIcon} label="Provedores" value={String(new Set(servers.map(s=>s.provider).filter(p=>p&&p!=="—")).size)} />
        </div>
      )}

      <AsyncBoundary loading={loading} error={error} skeleton={<div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} />)}</div>}>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {servers?.map((s) => <ServerCard key={s.id} server={s} />)}
          <button
            onClick={() => setOpen(true)}
            data-testid="server-add-slot"
            className="flex min-h-[200px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-line text-muted-foreground transition-colors hover:border-brand/40 hover:text-foreground"
          >
            <Plus className="h-6 w-6" />
            <span className="text-sm font-medium">Adicionar servidor</span>
            <span className="text-xs text-muted-foreground">Cadastrar servidor real e emitir bootstrap</span>
          </button>
        </div>
      </AsyncBoundary>
    </div>
  );
}
